// Maximum valid JavaScript date value, used to represent 'never expires'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useReducer, useState } from "react";

import { ITimeEntry, ETimesheetEntryStatus, ETimesheetEntryType } from "../interfaces/timeEntry";
import {
    ChangeStatus,
    EBookingStatus,
    ECalendarStatus,
    ESystemStatus,
    EWorkWorderType,
    IBookableResourceBooking,
    IEvent,
    IEventBase,
    IOtherSiteEvent
} from "../interfaces/event";
import {
    addDays,
    addMinutes,
    differenceInDays,
    differenceInMinutes, endOfDay,
    endOfWeek,
    format,
    isAfter,
    isBefore,
    isEqual,
    isSameDay, areIntervalsOverlapping,
    isWithinInterval, startOfDay,
    startOfWeek, subDays, subMinutes
} from "date-fns";
import { IProviderProps } from "../interfaces/provider";
import { apiContext } from "./api";
import { limitDateRangeToOneYear, otherSiteShiftFormatter, shiftsFormatter } from "../helpers/shifts/shiftsFormatter";
import Swal from "sweetalert2";
import { useTheme } from "@fluentui/react";
import { useIncidentTypes } from "../hooks/incidentType";
import { resourcesContext } from "./resources";
import { EEventStatus } from "../helpers/calendar";
import { toast } from "../helpers/notifications";
import { withRetry } from "../helpers/retry";
import {
    IAvailability,
    IAvantCareEnvironmentInfo,
    IBookableResourceRosterValidationResponse,
    IBookingStatusResponse,
    IDraftSavedItem,
    ILinkedError,
    IOtherSiteWorkOrder,
    IPayrollHourVariation,
    IPayrollHourVariationResponse,
    IPublishShiftsRequestViewModel,
    IResourceData,
    IRosteringBookingViewModel,
    IRosterValidationResponse,
    IServiceResponse,
    ISiteLocation,
    ITimeOffRequest,
    IWorkOrder,
    IWorkOrderType,
    RosterValidationErrorType,
    ShiftCancellationRequestStatus,
    JobType,
    statecode
} from "../interfaces/resource";
import { IIncidentType } from "../interfaces/incidentTypes";
import { TView } from "../components/scheduler/Scheduler";
import { authContext } from "./auth";
import { IDraft } from "../interfaces/draft";
import { IAvantCareConfigurationModel } from "../interfaces/avantcareConfig";

export enum DraftStatus {
    pending,
    synced,
    edited,
    empty,
    publishing,
    found,
}

export enum EPublishStatus {
    cancelled = 'cancelled',
    no_shift = 'noshift',
    network_error = 'networkerror',
    publish_error = 'publisherror',
    error = 'error',
    success = 'success',
}

export enum EEventToDBMap {
    isJobBidding = 'illumina_openforshiftjobbidding',
    start = 'msdyn_timewindowstart',
    end = 'msdyn_timewindowend',
    description = 'msdyn_workordersummary',
    shiftLength = 'msdyn_totalestimatedduration',
    bookableResourceStatus = 'BookingStatus@odata.bind',
    status = 'msdyn_systemstatus',
}

export const MAX_JS_DATE = 8640000000000000;

interface IShiftContext {
    loader: { loading: boolean, message: string }
    getShiftsForCalendarView: () => IEvent[]
    getOtherSiteShiftsForCalendarView: () => IOtherSiteEvent[]
    showOtherSiteShifts: boolean
    toggleShowOtherSiteShifts: () => void
    setRosterboardDragActive: (active: boolean) => void
    setCalendarDates: (startDate: Date, type: TView) => void
    startDate: Date,
    endDate: Date,
    moveShift: (shiftId: string, toResourceId: string | undefined, skipHistory?: boolean | undefined) => Promise<boolean>
    getShift: (id: string) => IEvent | undefined
    draftStatus: DraftStatus,
    getResourceData: (id: string) => IResourceData
    checkShiftLocationIsValid: (startDate: Date, endDate: Date, eventId?: string, resourceId?: string, originalResourceId?: string) => EEventStatus
    updateShiftInfo: (shiftId: string, data: Partial<IEvent>) => void
    checkShiftUnsavedChanges: () => boolean
    markAllShiftsAsUnchanged: () => void
    loadDraft: () => void
    saveDraft: () => void
    deleteDraft: () => void
    // Fetch and resolve the payroll cycle for a given date and view.
    getPayrollData: (targetDate?: Date, viewOverride?: TView) => Promise<{ cycle: PayrollCycleRecord, startForView: Date, endForView: Date } | undefined>

    validateAndPublishShifts: () => void;
    validateAllShifts: () => void;
    publishShift: (shiftId: string) => Promise<any>;
    undoShiftMove: () => void
    reloadShifts: () => void
    removeShift: (shiftId: string) => void;

    calculateCost: () => void;
    checkRequiredCharacteristics: (shiftId: string, resourceId: string) => boolean
    defineUserAvailability: (dateStart: Date, dateEnd: Date, cResourceId: string, eventId: string) => IAvailability[]
    checkIsModified: (preferredResourceId: string, resourceId: string) => ChangeStatus
    getResourceShifts: (resourceId: string) => IEvent[];
    getEnvironmentInfo: () => IAvantCareEnvironmentInfo;
    checkIfShiftAllowed: (event: IEvent) => void;
    publishSingleShift: (event?: IEvent, isConfirmation?: boolean) => Promise<{ status: EPublishStatus, updatedEventDetails?: Partial<IEvent> }>;
    patchShift: (shiftId: string, shiftData: Partial<IEvent>, isReloadShifts?: boolean) => Promise<any>;
    createLateClientCancelTimeEntry: (shift: IEvent) => Promise<any>;
    approveRejectCancellationRequest: (event: IEvent, approved: boolean, comments: string) => Promise<boolean>;
    view: TView
    setView: (view: TView) => void
    loadingPayrollData: boolean
    siteLocation?: string
    validated: boolean,
    // The full payroll cycle currently shown in the header/selector.
    currentPayrollRange: { start: Date, end: Date } | null
    getShiftTypes: () => IWorkOrderType[];
    getBookingStatusList: () => Promise<IBookingStatusResponse[]>;
    isCancellationReviewPending: (shift: IEvent | undefined) => boolean;
    payrollCycles: PayrollCycleRecord[];
}

// Store fetched payroll cycles in a list so we can compare dates and find the payroll cycle which contains the current start date.
// This lets us refer to the list when we want to update UI, without fetching payroll records to see what the dates are.
interface PayrollCycleRecord {
    id: string
    name?: string
    startDate: Date
    endDate: Date
}

interface IHistoryStore {
    eventId: string
    resourceId: string
    fromResourceID: string
    calendarStatus: ECalendarStatus
    lastUpdated: Date
}

const shiftsContext = createContext<IShiftContext>({
    loader: { loading: false, message: '' },
    draftStatus: DraftStatus.empty,
    getShiftsForCalendarView: () => ([]),
    getOtherSiteShiftsForCalendarView: () => ([]),
    showOtherSiteShifts: false,
    toggleShowOtherSiteShifts: () => {
    },
    setRosterboardDragActive: () => { },
    startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
    endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
    setCalendarDates: (startDate: Date, type) => {
    },
    moveShift: async () => false,
    getShift: (id: string) => {
        return undefined
    },
    getPayrollData: async () => undefined,
    updateShiftInfo: (shiftId: string, data: Partial<IEvent>) => {
    },
    getResourceData: () => { return null as any; },
    getResourceShifts: (resourceId: string) => {
        return [];
    },
    checkShiftLocationIsValid: () => 2,
    checkShiftUnsavedChanges: () => false,
    markAllShiftsAsUnchanged: () => {},
    loadDraft: () => {
    },
    undoShiftMove: () => {
    },
    removeShift: (shiftId: string) => { },
    saveDraft: () => {
    },
    validateAndPublishShifts: () => {
    },
    validateAllShifts: () => { },
    deleteDraft: () => {
    },
    reloadShifts: () => {
    },
    checkRequiredCharacteristics: () => false,
    defineUserAvailability: () => [],
    checkIsModified: () => 0,
    calculateCost: () => {
    },
    getEnvironmentInfo: () => null as any,
    checkIfShiftAllowed: () => {
    },
    publishShift: (shiftId: string) => {
        return null as any;
    },
    publishSingleShift: async (event?: IEvent, isConfirmation?: boolean) => ({ status: EPublishStatus.error }),
    patchShift: async (shiftId: string, shiftData: Partial<IEvent>) => {
    },
    createLateClientCancelTimeEntry: async (shift: IEvent) => { },
    view: "resourceTimelineFortnight",
    setView: () => {
    },
    loadingPayrollData: false,
    validated: false,
    currentPayrollRange: null,
    getShiftTypes: () => { return []; },
    approveRejectCancellationRequest: async (event: IEvent, approved: boolean, comments: string) => { return true; },
    getBookingStatusList: () => { return null as any; },
    isCancellationReviewPending: (shift: IEvent | undefined) => { return false; },
    payrollCycles: [],
})

type EventsArrayType = IEvent[]
type EventsObjectType = { [key: string]: IEvent }

type ShiftStoreReducerSetActionType = { set: EventsArrayType | EventsObjectType, update?: never, delete?: never }
type ShiftStoreReducerUpdateActionType = { set?: never, update: EventsArrayType | EventsObjectType, delete?: never }
type ShiftStoreReducerDeleteActionType = { set?: never, update?: never, delete: string[] }

type ShiftStoreReducerActionType = ShiftStoreReducerSetActionType | ShiftStoreReducerUpdateActionType | ShiftStoreReducerDeleteActionType

function ShiftStoreReducerSet(previousState: IEvent[], events: EventsArrayType | EventsObjectType) {
    if (Array.isArray(events)) {
        return events;
    } else if (typeof events === "object") {
        return Object.values(events);
    }
    return previousState;
}

function ShiftStoreReducerUpdate(previousState: IEvent[], events: EventsArrayType | EventsObjectType) {
    const newState = [...previousState]
    const indexById = new Map<string, number>()
    newState.forEach((event, index) => indexById.set(event.id, index))

    if (Array.isArray(events)) {
        events.forEach(event => {
            const existingIndex = indexById.get(event.id)
            if (existingIndex !== undefined) {
                newState[existingIndex] = event
            } else {
                newState.push(event)
                indexById.set(event.id, newState.length - 1)
            }
        })
    } else {
        Object.keys(events).forEach(key => {
            const existingIndex = indexById.get(key)
            if (existingIndex !== undefined) {
                newState[existingIndex] = (events as EventsObjectType)[key]
            } else {
                newState.push((events as EventsObjectType)[key])
                indexById.set(key, newState.length - 1)
            }
        })
    }

    return newState
}

function ShiftStoreReducerDelete(previousState: IEvent[], ids: string[]) {
    return previousState.filter(x => !ids.includes(x.id))
}

function ShiftStoreReducer(previousState: IEvent[], data: ShiftStoreReducerActionType) {
    if (!data) {
        return previousState;
    }

    if (data.set) {
        return ShiftStoreReducerSet(previousState, data.set);
    }

    if (data.update) {
        return ShiftStoreReducerUpdate(previousState, data.update);
    }

    if (data.delete) {
        return ShiftStoreReducerDelete(previousState, data.delete);
    }

    return previousState;
}

type IntervalDateInput = Date | string | number | null | undefined;

// Normalize interval inputs so date-fns never receives an invalid start/end pair.
function normalizeIntervalBounds(startInput: IntervalDateInput, endInput: IntervalDateInput): { start: Date, end: Date } | null {
    const start = new Date(startInput as any);
    const end = new Date(endInput as any);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return null;
    }
    return start <= end ? { start, end } : { start: end, end: start };
}

// Guarded wrapper around isWithinInterval to avoid transient "Invalid interval" runtime errors.
function safeIsWithinInterval(dateInput: IntervalDateInput, startInput: IntervalDateInput, endInput: IntervalDateInput): boolean {
    const date = new Date(dateInput as any);
    if (Number.isNaN(date.getTime())) {
        return false;
    }
    const interval = normalizeIntervalBounds(startInput, endInput);
    if (!interval) {
        return false;
    }
    return isWithinInterval(date, interval);
}


const ShiftsProvider = (props: IProviderProps) => {
    let initialAvantCareEnvInfo: IAvantCareEnvironmentInfo; //Need the envinfo on page load before setAvantCareEnvInfo value is available
    const theme = useTheme()
    const [cStartDate, setCStartDate] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }))
    const [cEndDate, setCEndDate] = useState<Date>(endOfWeek(new Date(), { weekStartsOn: 1 }))
    const [view, setView] = useState<TView>('resourceTimelineFortnight');

    const [loadedDates, setLoadedDates] = useState<boolean>(false)

    const [shiftStore, setShiftStore] = useReducer(ShiftStoreReducer, [])
    const shiftStoreRef = useRef(shiftStore);
    const availabilityCacheRef = useRef<{ key: string; map: Map<string, EEventStatus> }>({ key: '', map: new Map() })
    const availabilityCacheRevisionRef = useRef(0)
    // Track which date range the stores currently represent to avoid stale reads.
    const loadedRangeRef = useRef<{ start: Date | null, end: Date | null }>({ start: null, end: null })
    const loadShiftsTokenRef = useRef(0);
    const loadShiftsInFlightRef = useRef<{ start: Date; end: Date; token: number } | null>(null)
    const rosterboardDragActiveRef = useRef(false)
    const deferredShiftUpdatesRef = useRef<Map<string, { expectedResourceId?: string, shift: IEvent }>>(new Map())
    const shiftStoreAccess = useMemo<{ [key: string]: IEvent }>(() => {
        const access: { [key: string]: IEvent } = {}
        shiftStore.forEach((shift) => {
            access[shift.id] = shift
        })
        return access
    }, [shiftStore])
    const [otherSiteShiftStore, setOtherSiteShiftStore] = useState<IOtherSiteEvent[]>([]);
    const [allShiftTypes, setAllShiftTypes] = useState([]);
    const allShiftTypesRef = useRef(allShiftTypes);
    const [showOtherSiteShifts, setShowOtherSiteShifts] = useState(false)

    const [loader, setLoading] = useState<{ loading: boolean, message: string }>({ loading: false, message: '' })
    const [avantCareEnvInfo, setAvantCareEnvInfo] = useState<IAvantCareEnvironmentInfo>()
    const [history, setHistory] = useState<IHistoryStore[]>([])
    const [draftStatus, setDraftStatus] = useState<DraftStatus>(DraftStatus.empty)
    const [draftId, setDraftId] = useState<string | undefined>()
    const [incidentTypes, reloadIncidentTypes] = useIncidentTypes([]);
    const allIncidentTypesRef = useRef(incidentTypes);
    const { get, post, postOrThrow, patch, remove } = useContext(apiContext)
    const [validateHack, setValidationHack] = useState(true)
    //const params = useParams<TUrlParams>()
    const { getResource, getAllResources, getLeaves, getResourcesRevision } = useContext(resourcesContext)
    const [reloadView, setReloadView] = useState(false)
    const { getAccount } = useContext(authContext)
    const [payrollNeedsUpdate, setPayrollNeedsUpdate] = useState(false);
    const [loadingPayrollData, setLoadingPayrollData] = useState(false);
    // Store the last fetched list of payroll cycles so we can resolve the correct cycle locally without needing another API call.
    const [payrollCycles, setPayrollCycles] = useState<PayrollCycleRecord[]>([]);
    // The currently selected payroll record.
    const [currentPayrollRange, setCurrentPayrollRange] = useState<{ start: Date, end: Date } | null>(null);
    // Ref lets us ignore stale async results when users click quickly or when a slower request returns after a newer one.
    const payrollRequestIdRef = useRef(0);
    const [siteLocation, setSiteLocation] = useState<string>();
    const [payrollHoursVariations, setPayrollHoursVariations] = useState<IPayrollHourVariation[]>();
    const scrollRestoreTokenRef = useRef(0);
    const captureScrollState = useCallback(() => {
        if (typeof window === "undefined") return null;
        const headerScroller = document.querySelector<HTMLElement>(".fc-scrollgrid-section-header .fc-scroller");
        const bodyScroller = document.querySelector<HTMLElement>(".fc-scrollgrid-section-body .fc-scroller");
        if (!headerScroller && !bodyScroller) return null;
        return {
            bodyTop: bodyScroller?.scrollTop ?? 0,
            bodyLeft: bodyScroller?.scrollLeft ?? 0,
            headerLeft: headerScroller?.scrollLeft ?? null,
        };
    }, []);
    const restoreScrollState = useCallback((state: { bodyTop: number; bodyLeft: number; headerLeft: number | null } | null) => {
        if (!state || typeof window === "undefined") return;
        const headerScroller = document.querySelector<HTMLElement>(".fc-scrollgrid-section-header .fc-scroller");
        const bodyScroller = document.querySelector<HTMLElement>(".fc-scrollgrid-section-body .fc-scroller");
        if (bodyScroller) {
            bodyScroller.scrollTop = state.bodyTop;
            bodyScroller.scrollLeft = state.bodyLeft;
        }
        if (headerScroller && state.headerLeft != null) {
            headerScroller.scrollLeft = state.headerLeft;
        }
    }, []);
    const scheduleScrollRestore = useCallback((state: { bodyTop: number; bodyLeft: number; headerLeft: number | null } | null) => {
        if (!state) return;
        const token = (scrollRestoreTokenRef.current += 1);
        const restoreIfLatest = () => {
            if (scrollRestoreTokenRef.current !== token) return;
            restoreScrollState(state);
        };
        requestAnimationFrame(() => {
            restoreIfLatest();
            requestAnimationFrame(restoreIfLatest);
        });
    }, [restoreScrollState]);
    const [validated, setValidated] = useState(false);
    const [bookingStatusList, setBookingStatusList] = useState<IBookingStatusResponse[]>(null as any);

    const ServiceSiteLocationType = 390950001; //Site location type in Work Order/Shift

    // Apply any queued updates after drag ends.
    const flushDeferredRosterboardShiftUpdates = () => {
        if (!deferredShiftUpdatesRef.current.size) return

        const updates: IEvent[] = []
        deferredShiftUpdatesRef.current.forEach((item, id) => {
            const current = shiftStoreRef.current.find((s) => s.id === id)
            if (!current) return
            if (item.expectedResourceId && current.resourceId !== item.expectedResourceId) return
            updates.push(item.shift)
        })
        deferredShiftUpdatesRef.current.clear()

        if (updates.length) {
            setShiftStore({ update: updates })
        }
    }

    // Toggle drag mode so updates can be deferred during drag.
    const setRosterboardDragActive = (active: boolean) => {
        rosterboardDragActiveRef.current = active
        if (!active) {
            flushDeferredRosterboardShiftUpdates()
        }
    }

    // Update immediately unless the user is dragging.
    const applyOrDeferShiftUpdate = (shift: IEvent, expectedResourceId?: string) => {
        if (rosterboardDragActiveRef.current) {
            deferredShiftUpdatesRef.current.set(shift.id, { expectedResourceId, shift })
            return
        }
        const scrollState = captureScrollState();
        setShiftStore({ update: [shift] })
        scheduleScrollRestore(scrollState);
    }

    useEffect(() => {
        allIncidentTypesRef.current = incidentTypes;
    }, [incidentTypes])

    useEffect(() => {
        allShiftTypesRef.current = allShiftTypes;
    }, [allShiftTypes])

    useEffect(() => {
        if (!loader.loading && payrollNeedsUpdate && !loadingPayrollData) {
            getPayrollData()
        }
    }, [payrollNeedsUpdate, loader])

    useEffect(() => {
        const loadPayrollCycle = () => {
            const localPayrollData = localStorage.getItem(`illuminance-scheduler-payroll-data_${getSiteId()}`);

            if (!localPayrollData) {
                getPayrollData();
                return;
            }
            const payrollData = JSON.parse(localPayrollData) as { start: Date, end: Date } | undefined;
            const hasPayrollData = payrollData && payrollData['start'] && payrollData['end'];
            if (!hasPayrollData) return;
            let payrollStart = new Date()
            let payrollEnd = new Date();
            const psd = new Date(payrollData['start']);
            const ped = new Date(payrollData['end']);
            const payrollLength = differenceInDays(ped, psd);
            if (safeIsWithinInterval(payrollStart, psd, ped)) {
                payrollStart = psd;
                payrollEnd = ped;
            }
            else {

                const diffToNow = differenceInDays(new Date(), new Date(payrollData['start']))
                const daysSinceStartOfPayroll = diffToNow % payrollLength;

                payrollStart.setDate(payrollStart.getDate() - daysSinceStartOfPayroll);
                payrollStart = startOfDay(payrollStart);

                payrollEnd.setTime(payrollStart.getTime());
                payrollEnd.setDate(payrollEnd.getDate() + payrollLength);
                payrollEnd = endOfDay(payrollEnd);
            }

            setCStartDate(payrollStart)
            setCEndDate(payrollEnd)
            setLoadedDates(true)
            setView(payrollLength > 7 ? 'resourceTimelineFortnight' : 'resourceTimelineWeek')

            setPayrollNeedsUpdate(true);

        }
        loadPayrollCycle()
    }, [])

    //Triggered when a new job added in parent iframe and a postMessage is sent
    useEffect(() => {
        window.addEventListener('message', (e: any) => {
            if (!e.data?.type || e.data.type != 'ac-rb') return;
            if (e.data?.jobId != undefined && e.data?.jobId != '') {
                let jobId = e.data.jobId.replace(/[{}]/g, '') //remove '{}' from id

            }
        })
    }, [])


    useEffect(() => {
        const checkForDraft = async () => {
            const account = getAccount();
            const siteId = getSiteId();
            const res = await get(`/Rostering/${account?.tenantId}/${account?.localAccountId}/${siteId}/drafts`)
            const draftList: IDraft[] = res.body
            const calDraft = draftList.find(draft => {
                const startDate = new Date(draft.start)
                return format(startDate, 'yyyy-MM-dd') === format(cStartDate, 'yyyy-MM-dd')
            })
            if (calDraft) {
                setDraftId(calDraft.id)
                setDraftStatus(DraftStatus.found)
            } else {
                setDraftStatus(DraftStatus.empty)
            }
        }
        if (loadedDates) {
            checkForDraft()
        }
    }, [loadedDates, cStartDate, cEndDate])

    useEffect(() => {
        if (!loadedDates) return;

        loadAvantCareEnvironment();
        loadSiteLocation();

        const hasLoadedRange = (
            loadedRangeRef.current.start != null &&
            loadedRangeRef.current.end != null
        );

        if (!hasLoadedRange || shiftStoreRef.current.length < 1) {
            // Fallback load only: payroll data missing or initial load failed.
            loadShifts();
        }
    }, [loadedDates])

    useEffect(() => {
        if (reloadView) {
            setReloadView(false)
        }
    }, [reloadView])

    // Keep the selector calendar highlight in sync with the current FullCalendar start date, as the user navigates.
    useEffect(() => {
        if (!payrollCycles?.length) {
            if (currentPayrollRange) {
                setCurrentPayrollRange(null);
            }
            return;
        }
        const match = payrollCycles.find(pc =>
            safeIsWithinInterval(cStartDate, startOfDay(pc.startDate), endOfDay(pc.endDate))
        );
        if (!match) {
            if (currentPayrollRange) {
                setCurrentPayrollRange(null);
            }
            return;
        }
        const cycleStart = startOfDay(match.startDate);
        const cycleEnd = endOfDay(match.endDate);
        if (
            !currentPayrollRange ||
            !isEqual(cycleStart, currentPayrollRange.start) ||
            !isEqual(cycleEnd, currentPayrollRange.end)
        ) {
            setCurrentPayrollRange({ start: cycleStart, end: cycleEnd });
        }
    }, [payrollCycles, cStartDate]);

    // Fetch payroll cycles around the target date and resolve the matching cycle.
    // Also calculate the exact range the board should display for the current view.
    const getPayrollData = async (targetDate?: Date, viewOverride?: TView): Promise<{ cycle: PayrollCycleRecord, startForView: Date, endForView: Date } | undefined> => {
        const requestId = ++payrollRequestIdRef.current;
        setLoadingPayrollData(true);
        const target = targetDate ?? new Date();
        const startParam = format(addDays(target, -31), 'yyyy-MM-dd');
        const endParam = format(addDays(target, 31), 'yyyy-MM-dd');

        try {
            const res = await get(
                `/rostering/${getSiteId()}/payrollcycles?start=${startParam}&end=${endParam}`,
            ) as IServiceResponse<{ id: string, name?: string, startDate: string, endDate: string }[]>

            if (requestId !== payrollRequestIdRef.current) {
                return;
            }
            if (!Array.isArray(res.body)) {
                console.info("Payroll cycles response was not an array.");
                setPayrollCycles([]);
                setCurrentPayrollRange(null);
                return;
            }
            // Normalize API response into Date-based records for safe comparisons.
            const mapped = res.body.map(pc => ({
                id: pc.id,
                name: pc.name,
                startDate: new Date(pc.startDate),
                endDate: new Date(pc.endDate),
            }));
            setPayrollCycles(mapped);

            const match = mapped.find(pc =>
                safeIsWithinInterval(target, startOfDay(pc.startDate), endOfDay(pc.endDate))
            );
            if (!match) {
                console.info("No payroll cycle matched the selected date.");
                setCurrentPayrollRange(null);
                return;
            }

            const cycleStart = startOfDay(match.startDate);
            const cycleEnd = endOfDay(match.endDate);
            const cycleLength = differenceInDays(cycleEnd, cycleStart) + 1;
            const resolvedView = viewOverride ?? view;

            // startForView/endForView are the exact range the board should display (week or full cycle).
            let startForView = cycleStart;
            let endForView = cycleEnd;
            if (resolvedView === "resourceTimelineWeek" && cycleLength > 7) {
                const offset = differenceInDays(target, cycleStart);
                const block = Math.floor(offset / 7);
                startForView = addDays(cycleStart, block * 7);
                endForView = endOfDay(addDays(startForView, 6));
            }

            setCalendarDates(startForView, resolvedView);
            setLoadedDates(true);

            // First-load only: sync top-level view state to the resolved full cycle.
            if (!targetDate && !viewOverride) {
                setCStartDate(cycleStart);
                setCEndDate(cycleEnd);
                setView(cycleLength > 7 ? 'resourceTimelineFortnight' : 'resourceTimelineWeek');
                localStorage.setItem(`illuminance-scheduler-payroll-data_${getSiteId()}`, JSON.stringify({
                    start: cycleStart,
                    end: cycleEnd
                }));
            }

            // Mark payroll data as fresh and publish the resolved cycle to UI state.
            // Return the resolved cycle plus the exact range the board should show.
            setPayrollNeedsUpdate(false);
            setCurrentPayrollRange({ start: cycleStart, end: cycleEnd });
            return { cycle: match, startForView, endForView };
        } catch (e) {
            console.info("Failed to fetch payroll cycles.", e);
            if (requestId === payrollRequestIdRef.current) {
                setCurrentPayrollRange(null);
            }
            return;
        } finally {
            // Only clear the loading flag for the latest request.
            if (requestId === payrollRequestIdRef.current) {
                setLoadingPayrollData(false);
            }
        }
    }

    const loadAvantCareEnvironment = async () => {
        const envInfo = await get('/AvantCare/environment') as IAvantCareEnvironmentInfo;
        setAvantCareEnvInfo(envInfo);
        initialAvantCareEnvInfo = envInfo;
    }
    const loadSiteLocation = async () => {
        const locationID = getSiteId();
        const siteLocation = await get(`/Entity/illumina_sitelocations/${locationID}`, { select: 'illumina_name' }) as ISiteLocation;
        document.title = `Roster board - ${siteLocation.illumina_name}`;
        setSiteLocation(siteLocation?.illumina_name)
    }

    const getEnvironmentInfo = () => {
        return avantCareEnvInfo as IAvantCareEnvironmentInfo;
    }
    const getSiteId = () => {
        const siteId = localStorage.getItem('siteid');
        // Fallback to a hard-coded site if no URL param/localStorage value is present; this can point to the wrong site.
        const locationID = siteId ?? "c6fe3919-fb8e-ec11-b400-0022489242ae"
        return locationID;
    }
    const getShiftTypes = () => {
        return allShiftTypes;
    };

    const getBookingStatusList = async (): Promise<IBookingStatusResponse[]> => {
        if (bookingStatusList == null) {
            var r = await get('/Entity/bookingstatuses', {
                filter: 'statecode eq 0'
            }) as { value: IBookingStatusResponse[] };
            setBookingStatusList(r.value);
            return r.value;
        }
        return bookingStatusList;
    }

    const CurrentSiteJobSelectQuery = 'msdyn_name,msdyn_systemstatus,msdyn_timewindowstart,msdyn_timewindowend,msdyn_timefrompromised,msdyn_timetopromised,_msdyn_preferredresource_value,msdyn_workordersummary,msdyn_bookingsummary,msdyn_serviceaccount,msdyn_instructions,illumina_jobtype,msdyn_msdyn_workorder_msdyn_requirementcharacteristic_WorkOrder,illumina_openforshiftjobbidding, msdyn_totalestimatedduration,_msdyn_workordertype_value,_msdyn_primaryincidenttype_value,_illumina_servicecontact_value';
    const CurrentSiteJobExpandQuery = `msdyn_msdyn_workorder_bookableresourcebooking_WorkOrder($select=name,_bookingstatus_value,_resource_value,modifiedon,illumina_cancellationrequeststatus,illumina_cancellationrequestcomments),msdyn_serviceaccount($select=name,accountnumber,address1_composite)`;

    const getShiftById = async (shiftId: string) => {
        const locationID = getSiteId();
        const response = await get(`/Entity/msdyn_workorders`, {
            select: CurrentSiteJobSelectQuery,
            expand: CurrentSiteJobExpandQuery,
            filter: `illumina_servicelocation eq ${ServiceSiteLocationType} and msdyn_systemstatus ne ${ESystemStatus.Canceled} and _illumina_sitelocationfromid_value eq ${locationID} and msdyn_workorderid eq '${shiftId}'`
        });
        let shifts = response.value as IWorkOrder[]

        return (shifts?.length > 0) ? shifts[0] : null;

    }

    // Re-fetch a single shift after a save so we stay in sync with the server.
    const refreshShiftFromApi = async (shiftId: string) => {
        const workOrder = await getShiftById(shiftId);
        if (!workOrder) {
            setShiftStore({ delete: [shiftId] });
            return;
        }

        const formatted = shiftsFormatter(
            workOrder,
            (allShiftTypesRef.current ?? []) as any,
            (allIncidentTypesRef.current ?? []) as any
        );

        if (!formatted) {
            setShiftStore({ delete: [shiftId] });
            return;
        }

        // Clear any transient loading flags before updating the store.
        setShiftStore({ update: [{ ...formatted, loading: false, validating: false }] });
    }
    const loadShifts = async (syncHistory?: boolean, startData: Date = cStartDate, endData: Date = cEndDate) => {
        const inFlight = loadShiftsInFlightRef.current;
        if (inFlight && isEqual(inFlight.start, startData) && isEqual(inFlight.end, endData)) {
            return;
        }
        const token = ++loadShiftsTokenRef.current;
        loadShiftsInFlightRef.current = { start: startData, end: endData, token };

        setValidated(false)
        // Scope stores to the requested range up front to avoid stale data.
        loadedRangeRef.current = { start: startData, end: endData }
        setShiftStore({ set: [] })
        setOtherSiteShiftStore([])
        const loadMessage = syncHistory ? 'Reloading Shifts' : 'Loading Shifts'
        setLoading({ loading: true, message: loadMessage });
        try {
        let apiIncidentTypes: IIncidentType[] = []
        if (syncHistory || (!incidentTypes || incidentTypes.length < 1)) {
            apiIncidentTypes = await reloadIncidentTypes()
        }

        //extra day for timezone issue. This will load extra shifts which will be excluded later with local date filter
        var sdStr = format(subDays(startData, 1), 'yyyy-MM-dd');
        var edStr = format(addDays(endData, 1), 'yyyy-MM-dd');
        var edEndOfTheDay = endOfDay(endData);

        const locationID = getSiteId();

        const shiftTypesPromise = get('/Entity/msdyn_workordertypes', {
            select: 'msdyn_name,illumina_displaycolour',
            filter: 'statecode eq 0'
        })
        const shiftsListPromise = get(
            '/Entity/msdyn_workorders',
            {
                select: CurrentSiteJobSelectQuery,
                filter: `illumina_servicelocation eq ${ServiceSiteLocationType} and msdyn_systemstatus ne ${ESystemStatus.Canceled} and _illumina_sitelocationfromid_value eq ${locationID} and (msdyn_timewindowstart ge ${sdStr} and msdyn_timewindowstart le ${edStr})`,
                expand: CurrentSiteJobExpandQuery,
                top:5000
            }
        );
        
        let dateFilter = `(illumina_payrollcycle/illumina_startdate ge '${sdStr}' and illumina_payrollcycle/illumina_startdate le '${edStr}') ` +
            `or (illumina_payrollcycle/illumina_enddate ge '${sdStr}' and illumina_payrollcycle/illumina_enddate le '${edStr}') ` +
            ` or (illumina_payrollcycle/illumina_startdate le ${sdStr} and illumina_payrollcycle/illumina_enddate ge ${edStr})`;

        const payrollHoursVariationsPromise = get('/Entity/illumina_payrollhourvariations', {
            filter: dateFilter,
            select: 'illumina_minimumnoofworkhoursperpayroll,illumina_maximumnoofworkhoursperpayroll,_illumina_payrollcycle_value,_illumina_bookableresource_value',
            expand: 'illumina_payrollcycle($select=illumina_startdate,illumina_enddate)'
        })
        
        const shiftsList = await shiftsListPromise;
        
        const payrollHoursVariations = await payrollHoursVariationsPromise;
        const shiftTypes = await shiftTypesPromise;
        setAllShiftTypes(shiftTypes?.value);
        if (payrollHoursVariations?.value) {
            setPayrollHoursVariations(payrollHoursVariations.value.map((x: IPayrollHourVariationResponse) => {
                return {
                    bookableResourceId: x._illumina_bookableresource_value,
                    maximumHoursPerPayroll: x.illumina_maximumnoofworkhoursperpayroll,
                    minimumHoursPerPayroll: x.illumina_minimumnoofworkhoursperpayroll,
                    payrollCycleId: x._illumina_payrollcycle_value,
                    payrollEnd: new Date(x.illumina_payrollcycle.illumina_enddate),
                    payrollStart: new Date(x.illumina_payrollcycle.illumina_startdate)
                } as IPayrollHourVariation;
            }));
        }

        const workOrders = shiftsList.value as IWorkOrder[];
        if (workOrders) {
            const sanitizedList = workOrders.map((shift: any) => shiftsFormatter(shift, shiftTypes?.value, apiIncidentTypes.length < 1 ? incidentTypes : apiIncidentTypes))
                .filter(shift => shift?.start && shift.start >= startData && shift.start <= edEndOfTheDay) as IEvent[]; //exclude any extra items outside of payroll cycle
            // If no shifts are returned for the range, clear the store to avoid stale data.
            if (sanitizedList.length < 1) {
                setShiftStore({ set: [] });
                setLoading({ loading: false, message: '' });
            } else if (syncHistory && (history && history.length > 0)) {
                // Apply any draft/history moves to the freshly loaded list before validation.
                const syncedShifts = syncHistoryToState(sanitizedList, history);
                validateShifts(syncedShifts, startData, endData).then(res => {
                    setShiftStore({ update: res });

                    setLoading({ loading: false, message: '' });
                })
            } else {
                validateShifts(sanitizedList, startData, endData).then(res => {
                    setShiftStore({ update: res })

                    setLoading({ loading: false, message: '' });
                })
            }
        } else {
            // No shifts returned from API; clear store to avoid stale data.
            setShiftStore({ set: [] });
            setLoading({ loading: false, message: '' });
        }

        getOtherSiteShifts(startData, endData, shiftTypes?.value);
        } catch (e: any) {
            toast('error', 'Failed to load shifts. Please try again.')
        } finally {
            // Defensive: if an exception occurred mid-load, ensure we don't leave the global loader stuck on.
            setLoading((prev) => (prev.message === loadMessage ? { loading: false, message: '' } : prev))
            if (loadShiftsInFlightRef.current?.token === token) {
                loadShiftsInFlightRef.current = null;
            }
        }
    }

    const getOtherSiteShifts = async (startData: Date, endData: Date, shiftTypes: IWorkOrderType[]) => {
        const sdStr = format(subDays(startData, 1), 'yyyy-MM-dd');
        const edStr = format(addDays(endData, 1), 'yyyy-MM-dd');
        const edEndOfTheDay = endOfDay(endData);
        const locationID = getSiteId();

        var shiftsOnOtherSites;
        var otherSiteWorkOrders: IOtherSiteWorkOrder[] = [];

        while (shiftsOnOtherSites === undefined || shiftsOnOtherSites["@odata.nextLink"] !== undefined) 
        {
            var skiptoken = "";
            
            if (shiftsOnOtherSites !== undefined && shiftsOnOtherSites["@odata.nextLink"] !== undefined)
            {
                var nextLink = shiftsOnOtherSites["@odata.nextLink"] as string;
                // Extract skiptoken from the nextLink
                if (nextLink.includes("$skiptoken=")) {
                    skiptoken = nextLink.split("&$skiptoken=")[1];
                }
            }

            const shiftsOnOtherSitesPromise = get(
                '/Entity/msdyn_workorders',
                {
                    select: 'msdyn_name,msdyn_timewindowstart,msdyn_timewindowend,msdyn_totalestimatedduration,msdyn_timefrompromised,msdyn_timetopromised,_msdyn_workordertype_value,illumina_jobtype,_illumina_sitelocationfromid_value,_illumina_servicecontact_value',
                    filter: `msdyn_systemstatus ne ${ESystemStatus.Canceled} and _illumina_sitelocationfromid_value ne ${locationID} and (msdyn_timewindowstart ge ${sdStr} and msdyn_timewindowstart le ${edStr})`,
                    expand: 'msdyn_msdyn_workorder_bookableresourcebooking_WorkOrder($select=name,_bookingstatus_value,_resource_value)',
                    top:5000,
                    skiptoken: skiptoken
                }
            )
            
            shiftsOnOtherSites = await shiftsOnOtherSitesPromise; 
            if (shiftsOnOtherSites !== null)
                otherSiteWorkOrders = otherSiteWorkOrders.concat(shiftsOnOtherSites.value as IOtherSiteWorkOrder[]);
        }
        
        
        // const otherSiteWorkOrders = shiftsOnOtherSites.value as IOtherSiteWorkOrder[]
        if (otherSiteWorkOrders) {
            const sanitizedList = otherSiteWorkOrders.map((shift) => otherSiteShiftFormatter(shift, shiftTypes))
                .filter(shift => shift !== undefined && shift?.start && shift.start >= startData && shift.start <= edEndOfTheDay) as IOtherSiteEvent[] //exclude any extra items outside of payroll cycle

            if (sanitizedList.length > 0) {
                setOtherSiteShiftStore(sanitizedList)
            } else {
                setOtherSiteShiftStore([])
            }
        } else {
            setOtherSiteShiftStore([])
        }
    }

    const moveShift = async (shiftId: string, toResourceId?: string, skipHistory?: boolean) => {
        let promise: Promise<void> | undefined = undefined;
        if (!shiftStore) {
            return false
        }
        const cpShifts = [...shiftStore]
        const index = cpShifts.findIndex(sh => sh.id === shiftId)

        if (index < 0) {
            return false
        }
        if (toResourceId) {
            const newShift = { ...cpShifts[index] };
            const oldResourceId = newShift.resourceId;
            const hasRequiredCharacteristics = checkRequiredCharacteristics(shiftId, toResourceId)
            const modifiedStatus = checkIsModified(newShift.originalResourceID, toResourceId)
            const oldCalendarStatus = newShift.calendarStatus

            // When a shift is dragged into the calendar
            if (toResourceId !== '0') {
                // Validate the time for the shift overlapping with the target support worker's approved leave.
                const leavesApprovedTargetResource = getResource(toResourceId);
                const hasOverlap = checkOverlapingWithLeavesRequest(leavesApprovedTargetResource?.approvedLeaves ?? [], newShift.start, newShift.end);
                // If it has overlapped, show an error message and bring the shift back to the previous support worker.
                if (hasOverlap) {
                    toast('error', 'This job cannot be assigned. The assigned support worker has an approved leave.');
                    newShift.resourceId = oldResourceId;
                    newShift.loading = false;
                    newShift.validating = false;
                    setShiftStore({ update: [newShift] });
                    return false;
                }
            }

            newShift.resourceId = toResourceId

            newShift.changeStatus = modifiedStatus
            newShift.loading = toResourceId !== '0'
            newShift.validating = toResourceId !== '0'

            if (!hasRequiredCharacteristics) {
                newShift.calendarStatus = ECalendarStatus.issue
            } else if (toResourceId === '0') {
                newShift.calendarStatus = ECalendarStatus.scheduled
            } else {
                newShift.calendarStatus = ECalendarStatus.assigned
            }
            if (toResourceId === '0') {
                newShift.calendarStatus = ECalendarStatus.scheduled
            }
            if (skipHistory == null || skipHistory == false)
                setHistory((prev) => {
                    return [...prev, {
                        resourceId: toResourceId,
                        eventId: shiftId,
                        lastUpdated: new Date(),
                        fromResourceID: oldResourceId ?? "0",
                        calendarStatus: oldCalendarStatus
                    }]
                })

            setDraftStatus(DraftStatus.edited)

            if (toResourceId !== '0') {
                promise = checkIfShiftMoveAllowed(newShift)
            }

            setShiftStore({ update: [newShift] })
        }

        if (promise) {
            await promise;
        }
        return true
    }

    const checkOverlapingWithLeavesRequest = (
        intervals: ITimeOffRequest[], 
        startTime: Date, 
        endTime: Date
    )=> {
        const inputStart = new Date(startTime);
        const inputEnd = new Date(endTime);
        
        for (const interval of intervals) {
            const intervalStart = new Date(interval.start);
            const intervalEnd = new Date(interval.end);
            
            if (
                (isAfter(inputStart, intervalStart) && isBefore(inputStart, intervalEnd)) ||
                (isAfter(inputEnd, intervalStart) && isBefore(inputEnd, intervalEnd)) ||
                (inputStart <= intervalStart && inputEnd >= intervalEnd) ||
                (intervalStart <= inputStart && intervalEnd >= inputEnd)
            ) {
                return true;
            }
        }
        
        return false;
    }

    const validateShifts = async (shifts: IEvent[], startDate: Date, endDate: Date): Promise<{ [key: string]: IEvent }> => {
        const cpShiftStore = [...shifts]
        let assignedShifts: IRosteringBookingViewModel[] = [];
        let updatedShifts: { [key: string]: IEvent } = {};
        let originalShifts: { [key: string]: IEvent } = {};
        let newShiftStore: { [key: string]: IEvent } = {};

        cpShiftStore.forEach(shift => {
            if (shift.resourceId && shift.resourceId != '' && shift.resourceId != '0') {
                assignedShifts.push({
                    id: shift.id,
                    jobId: shift.id,
                    tempId: '',
                    start: shift.start,
                    end: shift.end,
                    bookableResourceId: shift.resourceId,
                    cost: 0,
                    status: shift.changeStatus,
                    bookingId: shift.BookingId
                })
                updatedShifts[shift.id] = {
                    ...shift,
                    validating: true
                }
                originalShifts[shift.id] = shift;
                return;
            }
            updatedShifts[shift.id] = shift;
        });

        setShiftStore({ update: updatedShifts })

        if (assignedShifts.length == 0) {
            setValidated(true)
            return newShiftStore
        }

        const validateMessage = 'Validating Shifts'
        setLoading({ loading: true, message: validateMessage });
        try {
            let response: IServiceResponse<IBookableResourceRosterValidationResponse[]>;
            try {
                response = (await withRetry(
                    () => postOrThrow(`/Rostering/validate`, {
                        shifts: assignedShifts,
                        siteId: localStorage.getItem('siteid'),
                        startDateFilter: startDate,
                        endDateFilter: endDate
                    }, undefined, 30000),
                    {
                        attempts: 3,
                        initialDelayMs: 300,
                        backoffFactor: 2,
                        maxDelayMs: 2000,
                    }
                )) as IServiceResponse<IBookableResourceRosterValidationResponse[]>;
            } catch (e: any) {
                await toast('error', 'Validation request failed. Please try again.');
                // Ensure we don't leave shifts stuck in validating state on network failures/timeouts.
                const resetUpdates: { [key: string]: IEvent } = {}
                Object.keys(originalShifts).forEach((id) => {
                    const current = shiftStoreRef.current.find((s) => s.id === id) ?? originalShifts[id]
                    resetUpdates[id] = { ...current, validating: false }
                })
                setShiftStore({ update: resetUpdates })
                return originalShifts
            }

            if (!response.success) {
                if (response.errors && response.errors[0]) {
                    await toast('error', response.errors[0]);
                }

                const resetUpdates: { [key: string]: IEvent } = {}
                Object.keys(originalShifts).forEach((id) => {
                    const current = shiftStoreRef.current.find((s) => s.id === id) ?? originalShifts[id]
                    resetUpdates[id] = { ...current, validating: false }
                })
                setShiftStore({ update: resetUpdates })
                return originalShifts
            }

            response.body.forEach(returnedShift => {
                if (originalShifts[returnedShift.id]) {
                    const updated = populateCalendarStatusBasedOnServiceResponse(originalShifts[returnedShift.id], returnedShift.errors, originalShifts[returnedShift.id].resourceId);
                    updated.validating = false
                    newShiftStore[returnedShift.id] = updated
                } else {
                    const newShift = populateCalendarStatusBasedOnServiceResponse(originalShifts[returnedShift.id], returnedShift.errors, originalShifts[returnedShift.id].resourceId);
                    newShift.validating = false
                    newShiftStore[newShift.id] = newShift;
                }

            });

            return newShiftStore
        } finally {
            // Clear the "Validating Shifts" overlay if it's still active for this operation.
            setLoading((prev) => (prev.message === validateMessage ? { loading: false, message: '' } : prev))
        }
    }

    const isCancellationReviewPending = (shift: IEvent | undefined) => {
        return (shift && shift.isCancellationRequestPendingOriginal && shift.resourceId != '0' && shift.resourceId == shift.originalResourceID) as boolean;
    }

    const checkIfShiftMoveAllowed = async (shift: IEvent) => {
        const shiftId = shift.id;
        const validatedResourceId = shift.resourceId;
        const snapshot = shiftStoreRef.current;

        const deletedShifts = snapshot.filter(x => x.id != shiftId && validatedResourceId != x.originalResourceID && validatedResourceId == x?.originalResourceID).map(x => {
            return { jobId: x.id, start: x.start, end: x.end, status: ChangeStatus.Deleted }
        });

        const newShifts = snapshot.filter(x => x.id != shiftId && x.resourceId == validatedResourceId && x.resourceId != x?.originalResourceID).map(x => {
            return { jobId: x.id, start: x.start, end: x.end, status: ChangeStatus.Modified }
        });
        let allChangedShifts = newShifts.concat(deletedShifts);
        // Clear validation flags if this request is the latest for the shift.
        const ensureValidationCleared = () => {
            const current = shiftStoreRef.current.find((s) => s.id === shiftId);
            if (!current) return;
            if (current.resourceId !== validatedResourceId) return;
            if (!current.loading && !current.validating) return;
            applyOrDeferShiftUpdate({ ...current, loading: false, validating: false }, validatedResourceId);
        };

        try {
            // Retry transient failures to reduce false validation errors.
            const response = (await withRetry(
                () => postOrThrow(
                    `/Rostering/bookableresource/${validatedResourceId}/isshiftAllowed?jobId=${shiftId}`,
                    allChangedShifts,
                    undefined,
                    15000
                ),
                {
                    attempts: 3,
                    initialDelayMs: 300,
                    backoffFactor: 2,
                    maxDelayMs: 2000,
                }
            )) as IServiceResponse<IRosterValidationResponse>;

            const currentShift = shiftStoreRef.current.find((s) => s.id === shiftId);
            if (!currentShift) return;
            if (currentShift.resourceId !== validatedResourceId) {
                // If the shift has moved again while the request was inflight, don't let the stale response
                // overwrite the latest position/status in the store.
                return;
            }

            if (response.success && response.body.status == 'Failed') {
                toast('error', response.body.errors[0].message);
            }

            const validatedShift = populateCalendarStatusBasedOnServiceResponse(currentShift, response.body.errors, validatedResourceId);
            validatedShift.loading = false
            validatedShift.validating = false
            applyOrDeferShiftUpdate(validatedShift, validatedResourceId)
        } catch (e: any) {
            const currentShift = shiftStoreRef.current.find((s) => s.id === shiftId);
            if (!currentShift) return;
            if (currentShift.resourceId !== validatedResourceId) return;

            // Defensive: ensure we don't leave the shift stuck in a loading/validating state.
            applyOrDeferShiftUpdate({ ...currentShift, loading: false, validating: false }, validatedResourceId)
            toast('error', 'Validation request failed. Please try again.');
        } finally {
            ensureValidationCleared();
        }
    }

    const getCalendarStatus = (shift: IEvent) => {
        if (shift.resourceId == '0') return ECalendarStatus.scheduled;
        const hasRequiredCharacteristics = checkRequiredCharacteristics(shift.id, shift.resourceId);
        return hasRequiredCharacteristics ? ECalendarStatus.assigned : ECalendarStatus.issue;
    }

    const populateCalendarStatusBasedOnServiceResponse = (shift: IEvent, errors: ILinkedError[], targetResourceId: string | undefined): IEvent => {
        const cpShift = { ...shift, exceededMaxWorkingHours: false, unavailable: false, missingSkills: false, isOverTime: false }
        if (shift.resourceId != targetResourceId) {
            return shift;
        }
        let envInfo = initialAvantCareEnvInfo || avantCareEnvInfo;
        const shiftErrorMessages: string[] = []
        var hasError = false;
        let errorValidationTypes = envInfo?.configuration.rosterBoardModule?.showHardAlertsOnlyFor;
        if (!errorValidationTypes) {
            errorValidationTypes = [RosterValidationErrorType.ExceededMaxWorkingHours, RosterValidationErrorType.ConflictBooking, RosterValidationErrorType.Unavailable, RosterValidationErrorType.Unknown];
        }
        //if resource id and target resource id doesn't match, it means the shift has been moved out of target resrouce since the FE was waiting for service call to finish
        errors.forEach((error) => {
            if (errorValidationTypes?.find(v => error.errorType == v)) {
                cpShift.calendarStatus = ECalendarStatus.error;
                hasError = true;
            }
            else if (!hasError) {
                cpShift.calendarStatus = ECalendarStatus.issue;
            }
            cpShift.exceededMaxWorkingHours = error.errorType == RosterValidationErrorType.ExceededMaxWorkingHours;
            cpShift.missingSkills = error.errorType == RosterValidationErrorType.MissingSkills;
            cpShift.isOverTime = error.errorType == RosterValidationErrorType.OverTime;
            shiftErrorMessages.push(error.message)
        });

        if (errors?.length == 0)
            cpShift.calendarStatus = (cpShift.resourceId === '0') ? ECalendarStatus.scheduled : ECalendarStatus.assigned

        cpShift.errors = shiftErrorMessages
        return cpShift

    }


    // Ensure the store represents the requested range before returning cached data.
    const isRangeLoaded = (startDate: Date, endDate: Date) => {
        if (!loadedRangeRef.current.start || !loadedRangeRef.current.end) return false
        return loadedRangeRef.current.start.getTime() === startDate.getTime()
            && loadedRangeRef.current.end.getTime() === endDate.getTime()
    }

    const getShiftsBetweenDates = (startDate: Date, endDate: Date): IEvent[] => {
        if (endDate < startDate) return []; //this can happen temporarily when enddate is set before start date in code
        if (!isRangeLoaded(startDate, endDate)) return [];
        // NOTE: `shiftStore` is loaded/filtered for the current view range by `loadShifts`.
        // Avoid re-filtering and re-allocating Dates on every render during heavy drag/drop.
        const leaves = (getLeaves() ?? []).filter((leave) =>
            areIntervalsOverlapping(
                { start: startDate, end: endDate },
                { start: leave.start, end: leave.end }
            )
        )
        return leaves.length > 0 ? shiftStore.concat(leaves) : shiftStore
    }

    const getOtherSiteShiftsBetweenDates = (startDate: Date, endDate: Date): IOtherSiteEvent[] => {
        if (endDate < startDate) return [];
        if (!isRangeLoaded(startDate, endDate)) return [];
        // NOTE: `otherSiteShiftStore` is loaded/filtered for the current view range by `getOtherSiteShifts`.
        return otherSiteShiftStore
    }

    const isBlockingLoad = loader?.loading && loader?.message === 'Loading Shifts'

    const getShiftsForView = (): IEvent[] => {
        if (isBlockingLoad) {
            return []
        }
        return getShiftsBetweenDates(cStartDate, cEndDate)
    }

    const getOtherSiteShiftsForView = (): IOtherSiteEvent[] => {
        if (isBlockingLoad) {
            return []
        }
        return getOtherSiteShiftsBetweenDates(cStartDate, cEndDate)
    }

    // const getShift = (id: string) => {
    //     const shift = shiftStore.findIndex((shift) => shift.id === id)
    //     return shift > -1 ? shiftStore[shift] : undefined
    // }

    const getShift = useCallback((id: string) => {
        let shift = shiftStoreAccess[id];
        if (shift) return shift;
        return getLeaves().find(x => x.id == id);
    }, [shiftStoreAccess])

    const validateAndPublishShifts = async () => {

        const confirmation = await Swal.fire({
            title: 'Are you sure you want to publish the schedule?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: theme.palette.themePrimary,
            cancelButtonColor: theme.palette.tealLight,
            confirmButtonText: 'Publish'
        })
        if (confirmation.isConfirmed) {
            const status = await publish()

            if (status) {
                const finalShifts: { [key: string]: IEvent } = {};
                shiftStore.forEach((shift) => {
                    if (shift.bookableResourceStatus !== EBookingStatus.Scheduled || shift.changeStatus !== ChangeStatus.UnChanged) {
                        finalShifts[shift.id] = {
                            ...shift,
                            bookableResourceStatus: EBookingStatus.Scheduled,
                            changeStatus: ChangeStatus.UnChanged
                        }
                    }
                })
                setShiftStore({ update: finalShifts })
                toast("success", 'Schedule was published successfully');
            }
        }


    }

    const validateAllShifts = async () => {

        const cpShiftStore = shiftStore;
        let allShifts: IRosteringBookingViewModel[] = [];
        let originalShifts: { [key: string]: IEvent } = {};
        let newShiftStore: { [key: string]: IEvent } = {};

        cpShiftStore.forEach(shift => {
            let isUnassigned = shift.resourceId == null || shift.resourceId == '' || shift.resourceId == '0';
            if (isUnassigned && (shift.originalResourceID == null || shift.originalResourceID == '' || shift.originalResourceID == '0')) return;
            allShifts.push({
                id: shift.id,
                jobId: shift.id,
                tempId: '',
                start: shift.start,
                end: shift.end,
                bookableResourceId: isUnassigned ? shift.originalResourceID : shift.resourceId,
                cost: 0,
                status: isUnassigned ? ChangeStatus.Deleted : shift.changeStatus,
                bookingId: shift.BookingId
            })
            originalShifts[shift.id] = shift;
        });

        if (allShifts.filter(x => x.bookableResourceId != null).length == 0) {
            setValidated(true)
            return;
        }

        const validateMessage = 'Validating Shifts'
        setLoading({ loading: true, message: validateMessage });
        try {
            // Use a longer timeout for batch validation.
            const response = (await postOrThrow(`/Rostering/validate`, {
                shifts: allShifts,
                siteId: localStorage.getItem('siteid'),
                // Include date range when validating all shifts to avoid DateTime error.
                startDateFilter: cStartDate,
                endDateFilter: cEndDate
            }, undefined, 30000)) as IServiceResponse<IBookableResourceRosterValidationResponse[]>;
            if (!response.success) {
                if (response.errors && response.errors[0]) {
                    await toast('error', response.errors[0]);
                }
                return;
            }

            response.body.forEach(returnedShift => {
                if (originalShifts[returnedShift.id]) {
                    newShiftStore[returnedShift.id] = populateCalendarStatusBasedOnServiceResponse(originalShifts[returnedShift.id], returnedShift.errors, originalShifts[returnedShift.id].resourceId);
                } else {
                    const newShift = populateCalendarStatusBasedOnServiceResponse(originalShifts[returnedShift.id], returnedShift.errors, originalShifts[returnedShift.id].resourceId);
                    newShiftStore[newShift.id] = newShift;
                }

            });

            setShiftStore({ update: newShiftStore })
        } catch (e: any) {
            await toast('error', 'Validation request failed. Please try again.');
        } finally {
            // Clear the global loader for this validation operation.
            setLoading((prev) => (prev.message === validateMessage ? { loading: false, message: '' } : prev))
        }
    }

    // Doesn't have a way of updating non-move/date changes (e.g. description)
    const publishSingleShift = async (shift?: IEvent, isConfirmation: boolean = true): Promise<{ status: EPublishStatus, updatedEventDetails?: Partial<IEvent> }> => {
        if (!shift) {
            return { status: EPublishStatus.no_shift };
        }
        try {
            if (isConfirmation) {
                const confirmation = await Swal.fire({
                    title: 'Are you sure you want to publish this shift?',
                    text: "You won't be able to revert this!",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: theme.palette.themePrimary,
                    cancelButtonColor: theme.palette.tealLight,
                    confirmButtonText: 'Publish'
                })
                
                if (!confirmation.isConfirmed) return { status: EPublishStatus.cancelled };
            }
            
            const shiftData: IRosteringBookingViewModel = {
                id: shift.id,
                jobId: shift.id,
                tempId: '',
                start: shift.start,
                end: shift.end,
                bookableResourceId: shift.resourceId === '0' ? '' : shift.resourceId,
                cost: 0,
                status: ChangeStatus.Modified,
                bookingId: shift.BookingId,
            };
            
            const data: IPublishShiftsRequestViewModel = {
                shifts: [shiftData],
                saveShiftsWithWarning: true,
                siteId: localStorage.getItem('siteid') ?? '',
            };
            
            const response = await post(`/Rostering/publish`, data) as IServiceResponse<IBookableResourceRosterValidationResponse[]>;

            if (response.success && response.body?.length > 0) {
                response.body.forEach(responseShift => {
                    const storedShift = shiftStore?.find(element => element.id === responseShift.id);
                    if (storedShift) {
                        populateCalendarStatusBasedOnServiceResponse(storedShift, responseShift.errors, storedShift.resourceId);
                    }
                });
                let errors = response.body.filter(b => b.errors?.length > 0).map(b => b.errors[0]);
                let errorMessages = errors.filter(e => e.message?.length > 0).map(e => e.message);

                toast('warning', 'Shift was not published. ' + errorMessages)
                return { status: EPublishStatus.publish_error };
            } else if (response.success) {
                const partialShift: IEvent = {
                    ...shift,
                    originalResourceID: shift.resourceId,
                    changeStatus: ChangeStatus.UnChanged
                }
                updateShiftInfo(shift.id, partialShift)
                toast('success', 'Shift was published successfully!')
                return { status: EPublishStatus.success, updatedEventDetails: partialShift };
            } else {
                toast('error', response.errors.length > 0 ? response.errors[0] : 'Shift was not published. There was an error whilst trying to publish')
                return { status: EPublishStatus.publish_error }
            }
        } catch (err) {
            console.error(err);
            toast('error', 'Shift not published: Network Error')
            return { status: EPublishStatus.network_error };
        }
    }


    // Doesn't have a way of updating non-move/date changes (e.g. description)
    //If not shifts provided, all shifts will be processed
    const publish = async (shifts?: IEvent[]): Promise<boolean> => {
        const prevDraftStatus = draftStatus;
        setDraftStatus(DraftStatus.publishing)

        if (shifts == null)
            shifts = shiftStore;

        const modifiedShifts: IRosteringBookingViewModel[] = [];
        shifts.forEach((shift) => {
            if (shift.changeStatus === ChangeStatus.Modified) {
                const shiftPublishData: IRosteringBookingViewModel = {
                    id: shift.id,
                    jobId: shift.id, //please provide job id and id the same
                    tempId: '', //for future usage. can be used for new shifts
                    start: shift.start,
                    end: shift.end,
                    bookableResourceId: shift.resourceId == '0' ? '' : shift.resourceId, //if unassigned set to empty string
                    cost: 0,
                    status: ChangeStatus.Modified, //For now, use 'modified' but in future, if we allow to add shifts from UI, then we can different status (like added, deleted et.) in here
                    bookingId: shift.BookingId
                }

                modifiedShifts.push(shiftPublishData);
            }
        })
        if (modifiedShifts.length < 1) {
            toast("warning", "No Shifts to Publish")
            setDraftStatus(prevDraftStatus)
            return false;
        }
        const response = (await post(`/Rostering/publish`, {
            shifts: modifiedShifts,
            saveShiftsWithWarning: true,
            siteId: localStorage.getItem('siteid')
        } as IPublishShiftsRequestViewModel)) as IServiceResponse<IBookableResourceRosterValidationResponse[]>;
        if (!response.success) {
            toast('error', response.errors[0]);
            setDraftStatus(prevDraftStatus)
            return false;
        }

        if (response.body.length > 0) {
            const updatedShifts: IEvent[] = [];
            response.body.forEach(rb => {
                const shift = shifts?.find(sh => sh.id === rb.id) as IEvent;
                let sf = populateCalendarStatusBasedOnServiceResponse(shift, rb.errors, shift.resourceId);
                updatedShifts.push(sf);
            });
            setShiftStore({ update: updatedShifts })
            toast("warning", 'Schedule was not published. There were issues during validation.');
            setDraftStatus(prevDraftStatus)
            return false;
        } else {
            deleteDraft()
            setHistory([])
            setDraftStatus(DraftStatus.empty)
            return true;
        }
    }

    const undoMove = () => {
        const cpHistory = [...history]
        if (cpHistory == null || cpHistory.length == 0) return;
        const editHistory = cpHistory.pop()
        if (!editHistory) return;
        const cpShiftStore = [...shiftStore]
        const index = cpShiftStore.findIndex((shift) => shift.id === editHistory.eventId)
        if (index > -1) {
            const shift = cpShiftStore[index];
            shift.resourceId = editHistory.fromResourceID;
            shift.changeStatus = shift.originalResourceID != shift.resourceId ? ChangeStatus.Modified : ChangeStatus.UnChanged;
            shift.displayCost = shift.changeStatus == ChangeStatus.UnChanged ? shift.actualCost : null;
            shift.calendarStatus = editHistory.calendarStatus
            setShiftStore({ update: [shift] })
        }
        setHistory(cpHistory)
    }

    const removeShift = (shiftId: string) => {
        setShiftStore({ delete: [shiftId] })
    }
    useEffect(() => {
        shiftStoreRef.current = shiftStore;
        availabilityCacheRevisionRef.current += 1 // Increment revision so availability cache can be invalidated
        availabilityCacheRef.current.key = ''
    }, [shiftStore])

    useEffect(() => {
        availabilityCacheRevisionRef.current += 1 // Increment revision so availability cache can be invalidated
        availabilityCacheRef.current.key = ''
    }, [otherSiteShiftStore])


    const deleteDraft = async () => {
        const storageKey = format(cStartDate, 'yyyy-MM-dd')
        localStorage.removeItem(storageKey)

        const start = format(cStartDate, 'yyyy-MM-dd');
        const end = format(cEndDate, 'yyyy-MM-dd');
        const draftKey = `${start}_${end}`;
        const existingDraftIds = draftId;
        let draftIds = existingDraftIds ? existingDraftIds.split(',') : [];
        if (draftIds.length > 0) {
            const account = getAccount();
            const siteId = getSiteId();

            const response = (await remove(`/Rostering/${account?.tenantId}/${account?.localAccountId}/${siteId}/drafts?draftIds=${draftIds.join(',')}`)) as IServiceResponse<boolean>
            if (response.success)
                localStorage.removeItem(draftKey);
        }

        loadShifts()
        setDraftStatus(DraftStatus.empty)
    }

    const loadDraft = async () => {
        const start = format(cStartDate, 'yyyy-MM-dd');
        const end = format(cEndDate, 'yyyy-MM-dd');
        const siteId = getSiteId();
        const draftKey = `${start}_${end}_${siteId}`;

        const account = getAccount();
        const response = (await get(`/Rostering/${account?.tenantId}/${account?.localAccountId}/${siteId}/drafts/${draftId}`)) as { history: IHistoryStore[], shiftStore: IEvent[] }
        setHistory(response.history);

        const cpShifts = [...shiftStore];
        const finalShifts: { [key: string]: IEvent } = {};

        response.shiftStore.forEach(shift => {
            finalShifts[shift.id] = {
                ...shift,
                start: new Date(shift.start),
                end: new Date(shift.end)
            }
        })

        cpShifts.forEach(shift => {
            const updatedShift = finalShifts[shift.id];
            if (!updatedShift) {
                finalShifts[shift.id] = {
                    ...shift,
                    resourceId: '0'
                }
            }
        })

        setShiftStore({ set: finalShifts })
    }

    const syncHistoryToState = (shifts: IEvent[] = shiftStore, funHistory: IHistoryStore[] = history) => {
        const cpShifts = [...shifts]
        const latestHistoryByEventId = new Map<string, IHistoryStore>()
        funHistory.forEach((his) => {
            const current = latestHistoryByEventId.get(his.eventId)
            if (!current) {
                latestHistoryByEventId.set(his.eventId, his)
                return
            }
            if (new Date(his.lastUpdated).getTime() > new Date(current.lastUpdated).getTime()) {
                latestHistoryByEventId.set(his.eventId, his)
            }
        })
        return cpShifts.map((shift): IEvent => {
            const historyToProcess = shift.resourceId === "0" ? latestHistoryByEventId.get(shift.id) : undefined
            const resourceId = historyToProcess ? historyToProcess.resourceId : shift.resourceId;
            return {
                ...shift,
                resourceId: resourceId,
                BookingId: shift.BookingId,
                calendarStatus: getCalendarStatus(shift),
                changeStatus: shift.originalResourceID != resourceId ? ChangeStatus.Modified : ChangeStatus.UnChanged

            } as IEvent;
        })
    }

    const reloadShifts = async () => {
        await loadShifts(true)
    }

    const saveDraft = async (): Promise<boolean> => {
        if (Array.isArray(history) && history.length > 0) {
            setDraftStatus(DraftStatus.synced)
            const account = getAccount();
            const siteId = getSiteId();
            const start = format(cStartDate, 'yyyy-MM-dd');
            const end = format(cEndDate, 'yyyy-MM-dd');
            const formData = new FormData();

            const data = {
                history,
                shiftStore
            }

            formData.append("file", JSON.stringify(data));
            formData.append("title", `${start}_${end}`); //TODO: Ask user for the name of the draft
            formData.append("start", start);
            formData.append("end", end);
            const response = (await post(`/Rostering/${account?.tenantId}/${account?.localAccountId}/${siteId}/drafts`, formData)) as IServiceResponse<IDraftSavedItem>
            if (response.success) {
                const draftKey = `${start}_${end}_${siteId}`;
                localStorage.setItem(draftKey, response.body.id);
                setDraftId(response.body.id)
            }
            return response?.success
        } else {
            toast('error', 'Unable to save draft as no changes have been made');
        }
        return false
    }

    const getShiftsInViewForResource = (startDate: Date, endDate: Date, resourceId?: string): IEvent[] => {
        const safeRange = normalizeIntervalBounds(startDate, endDate);
        if (!safeRange) return [];
        return shiftStore.filter(shift => {
            const foundResource = shift.resourceId === resourceId
            const withinInterval = safeIsWithinInterval(shift.start, safeRange.start, safeRange.end);

            return foundResource && withinInterval
        })
    }

    const getOtherSiteShiftsForResource = (startDate: Date, endDate: Date, resourceId?: string): IOtherSiteEvent[] => {
        const safeRange = normalizeIntervalBounds(startDate, endDate);
        if (!safeRange) return [];
        return otherSiteShiftStore.filter(shift => {
            const foundResource = shift.resourceId === resourceId
            const withinInterval = safeIsWithinInterval(shift.start, safeRange.start, safeRange.end);

            return foundResource && withinInterval
        })
    }

    // Pre-index shifts by resource for faster lookups in the UI.
    const resourceShiftsById = useMemo(() => {
        const map = new Map<string, IEvent[]>()
        shiftStore.forEach((shift) => {
            const id = shift.resourceId ?? '0'
            const list = map.get(id)
            if (list) {
                list.push(shift)
            } else {
                map.set(id, [shift])
            }
        })
        return map
    }, [shiftStore])

    // Same indexing for other-site shifts.
    const otherSiteShiftsById = useMemo(() => {
        const map = new Map<string, IOtherSiteEvent[]>()
        otherSiteShiftStore.forEach((shift) => {
            const id = shift.resourceId ?? '0'
            const list = map.get(id)
            if (list) {
                list.push(shift)
            } else {
                map.set(id, [shift])
            }
        })
        return map
    }, [otherSiteShiftStore])

    const checkResourceForTimeClash = (resourceId: string, startDate: Date, endDate: Date) => {
        //take one mins on/off to allow shifts like 8am-4pm and then 4pm-8pm etc.
        startDate = addMinutes(startDate, 1);
        endDate = subMinutes(endDate, 1);
        const selectionRange = normalizeIntervalBounds(startDate, endDate);
        if (!selectionRange) {
            return []; // Guard against transient/invalid ranges during navigation.
        }
        const resourceEvents: IEventBase[] = [
            ...(resourceShiftsById.get(resourceId) ?? []),
            ...(otherSiteShiftsById.get(resourceId) ?? [])
        ]
        return resourceEvents.filter(shift => {
            let within = false;
            within =
                safeIsWithinInterval(selectionRange.start, shift.start, shift.end)
                ||
                safeIsWithinInterval(selectionRange.end, shift.start, shift.end)
                ||
                safeIsWithinInterval(shift.start, selectionRange.start, selectionRange.end)
                ||
                safeIsWithinInterval(shift.end, selectionRange.start, selectionRange.end)

            return within
        });

    }

    const markAllShiftsAsUnchanged = () => {
        shiftStore.forEach((shift) => {
            if (shift.changeStatus === ChangeStatus.Modified) {
                shift.changeStatus = ChangeStatus.UnChanged;
            }
        });
    }

    const checkShiftUnsavedChanges = (): boolean => {
        let isChanged = false;
        shiftStore.forEach((shift) => {
            if (shift.changeStatus === ChangeStatus.Modified) {
                isChanged = true;
            }
        });
        return isChanged;
    }

    const checkShiftLocationIsValid = (startDate: Date, endDate: Date, eventId?: string, resourceId?: string, originalResourceId?: string): EEventStatus => {
        if (!resourceId || resourceId === '0' || resourceId === originalResourceId || !eventId) {
            return EEventStatus.valid
        }
        let hardErrorTypes = avantCareEnvInfo?.hardErrorTypes.map((et) => RosterValidationErrorType[et as keyof typeof RosterValidationErrorType])
        let isUnavailableHardError = hardErrorTypes?.indexOf(RosterValidationErrorType.Unavailable) != -1
        let isConflictShiftsHardError = hardErrorTypes?.indexOf(RosterValidationErrorType.ConflictBooking) != -1
        let isCharacteristicHardError = hardErrorTypes?.indexOf(RosterValidationErrorType.MissingSkills) != -1

        let leaves = getLeaves().filter(x => x.resourceId == resourceId)
            .filter(x => areIntervalsOverlapping({ start: startDate, end: endDate }, { start: x.start, end: x.end }));
        if (leaves.length > 0 && isUnavailableHardError)
            return EEventStatus.error;



        const resourcesShifts = checkResourceForTimeClash(resourceId, startDate, endDate);
        const bookableResource = getResource(resourceId);
        if ((!bookableResource || !bookableResource.availabilities) && isUnavailableHardError) {
            return EEventStatus.error
        }
        const isAvailable = bookableResource?.availabilities?.find(x => x.timeCode == 'Available' && x.start <= startDate && x.end >= endDate) != null
        if (!isAvailable && isUnavailableHardError) return EEventStatus.error;

        if (resourcesShifts?.length > 0 && isConflictShiftsHardError) {
            return EEventStatus.error;
        }
        let hasMetCharacteristics = checkRequiredCharacteristics(eventId, resourceId);
        return hasMetCharacteristics ? EEventStatus.valid : (isCharacteristicHardError ? EEventStatus.error : EEventStatus.characteristicsIssue);
    }

    const checkRequiredCharacteristics = (shiftId: string, resourceId: string): boolean => {
        const shift = getShift(shiftId);
        const resource = getResource(resourceId);
    
        if ((shift?.incidentType?.characteristic?.length ?? -1) === 0) {
            return true;
        }
    
        let characteristicsExpired = false;
    
        shift?.incidentType?.characteristic?.filter(incidentCharacteristic => incidentCharacteristic.status === statecode.Active).some(incidentCharacteristic => {
            let today = new Date();

            return resource?.characteristics?.some(resourceCharacteristic => {
                const isNameMatch = resourceCharacteristic?.characteristicId === incidentCharacteristic.id;
                // If expiryDate is null, treat as never expires (set to far future date)
                var expiryDate = resourceCharacteristic.expiryDate == null ? new Date(MAX_JS_DATE) : new Date(resourceCharacteristic.expiryDate);
                var shiftEndDate = shift.end == null ? new Date(0) : new Date(shift.end);

                // resourceCharacteristic.expiryDate == null, means that never expires (AC product design, need to let clients know about this behavior)
                const isExpired = expiryDate < shiftEndDate;

                if (isNameMatch && isExpired) {
                    characteristicsExpired = true;
                    return true; // Break out of the inner loop once an expired characteristic is found
                }

                return false;
            });
        });
    
        const characteristicsMet = !characteristicsExpired && !!shift?.incidentType?.characteristic?.filter(incidentCharacteristic => incidentCharacteristic?.status === statecode.Active).every(incidentCharacteristic =>
            resource?.characteristics?.find(resourceCharacteristic =>
                resourceCharacteristic.characteristicId === incidentCharacteristic.id
            )
        );
    
        return characteristicsMet;
    };

    const checkIsModified = (originalResourceID: string, resourceId: string) => {
        if (!originalResourceID || originalResourceID === "0") {
            if (resourceId === "0") {
                return ChangeStatus.UnChanged
            } else {
                return ChangeStatus.Modified
            }
        } else {
            if (originalResourceID === resourceId) {
                return ChangeStatus.UnChanged
            } else {
                return ChangeStatus.Modified
            }
        }
    }

    const getResourcesData = (resourceId: string) => {
        let resource = getResource(resourceId);
        const resourcesShifts = resourceShiftsById.get(resourceId) ?? []
        const otherSiteResourceShifts = otherSiteShiftsById.get(resourceId) ?? []
        const shiftsCount = resourcesShifts.filter(shift=>shift.workOrderType?.msdyn_name?.toLowerCase() !== 'unpaid break').length
        const allSitesShiftsCount = resourcesShifts.filter(shift=>shift.workOrderType?.msdyn_name?.toLowerCase() !== 'unpaid break').length + otherSiteResourceShifts.filter(shift=>shift.workOrderType?.msdyn_name?.toLowerCase() !== 'unpaid break').length
        let workDuration = 0;
        // Always work with a safe current range so leave/contract calculations cannot throw on transient date state.
        const safeCurrentRange = normalizeIntervalBounds(cStartDate, cEndDate);
        if (!safeCurrentRange) {
            return {
                maxHoursAllowedPerPayroll: resource?.maximumWorkHourPerPayroll,
                maxShiftsAllowedPerPayroll: resource?.maximumWorkHourPerPayroll,
                maxHoursPerShift: resource?.maxHoursPerShift,
                hoursWorked: 0,
                allSitesHoursWorked: 0,
                shifts: shiftsCount,
                allSitesShifts: allSitesShiftsCount,
                requirements: [],
                totalPay: 0,
                minimumWorkHoursPerPayroll: resource?.minimumWorkHoursPerPayroll,
            } as IResourceData;
        }

        resource?.approvedLeaves.forEach((leave) => {
            if (safeIsWithinInterval(leave.start, safeCurrentRange.start, safeCurrentRange.end)
                || safeIsWithinInterval(leave.end, safeCurrentRange.start, safeCurrentRange.end)
                || safeIsWithinInterval(safeCurrentRange.start, leave.start, leave.end)
                || safeIsWithinInterval(safeCurrentRange.end, leave.start, leave.end))
            {
                const leaveStart = new Date(leave.start);
                const leaveEnd = new Date(leave.end);
                let startDate = leaveStart < safeCurrentRange.start ? safeCurrentRange.start : leaveStart;
                let endDate = leaveEnd > safeCurrentRange.end ? safeCurrentRange.end : leaveEnd;

                resource?.contractedHours?.forEach(item => {
                    if (item.illumina_repeatpattern == RepeatPattern.Daily) {
                        workDuration += sumDailyHours(item, startDate, endDate);
                    } else if (item.illumina_repeatpattern == RepeatPattern.Weekly) {
                        workDuration += sumWeeklyHours(item, startDate, endDate);
                    } else if (item.illumina_repeatpattern == RepeatPattern.Fortnightly) {
                        workDuration += sumFortnightlyHours(item, startDate, endDate);
                    } else {
                        workDuration += calculateContractedHoursPerDay(item.illumina_startdate, item.illumina_enddate, startDate, endDate);
                    }
                })
            }
        })
        let totalPay = 0;
        resourcesShifts.filter(shift=>shift.workOrderType?.msdyn_name?.toLowerCase() !== 'sleepover' && shift.workOrderType?.msdyn_name?.toLowerCase() !== 'on call' && shift.workOrderType?.msdyn_name?.toLowerCase() !== 'unpaid break').forEach((shift) => {
            workDuration = workDuration + differenceInMinutes(
                new Date(shift.end) ?? new Date(),
                new Date(shift.start) ?? new Date(),
            );
            if (shift.displayCost)
                totalPay += shift.displayCost;
        })
        
        let allSitesHoursWorked = workDuration;
        otherSiteResourceShifts.filter(shift=>shift.workOrderType?.msdyn_name?.toLowerCase() !== 'sleepover' && shift.workOrderType?.msdyn_name?.toLowerCase() !== 'on call' && shift.workOrderType?.msdyn_name?.toLowerCase() !== 'unpaid break').forEach((shift) => {
            allSitesHoursWorked = allSitesHoursWorked + differenceInMinutes(
                new Date(shift.end) ?? new Date(),
                new Date(shift.start) ?? new Date(),
            );
        });

        let payrollVariation = payrollHoursVariations?.find(x => x.bookableResourceId.toLocaleLowerCase() == resourceId.toLocaleLowerCase() && isSameDay(x.payrollStart, safeCurrentRange.start) && isSameDay(x.payrollEnd, safeCurrentRange.end))

        return {
            maxHoursAllowedPerPayroll: payrollVariation && payrollVariation.maximumHoursPerPayroll ? payrollVariation.maximumHoursPerPayroll : resource?.maximumWorkHourPerPayroll,
            maxShiftsAllowedPerPayroll: resource?.maximumWorkHourPerPayroll,
            maxHoursPerShift: resource?.maxHoursPerShift,
            hoursWorked: workDuration / 60,
            allSitesHoursWorked: allSitesHoursWorked / 60,
            shifts: shiftsCount,
            allSitesShifts: allSitesShiftsCount,
            requirements: [],
            totalPay,
            minimumWorkHoursPerPayroll: payrollVariation && payrollVariation.minimumHoursPerPayroll ? payrollVariation.minimumHoursPerPayroll : resource?.minimumWorkHoursPerPayroll,
        } as IResourceData
    }
    const getResourceShifts = (resourceId: string) => {
        return resourceShiftsById.get(resourceId) ?? []
    }

    const setCalendarDates = (startDate: Date, type: TView) => {
        let endDate: Date
        if (type === "resourceTimelineFortnight") {
            endDate = new Date(startDate.getTime())
            endDate.setDate(endDate.getDate() + 13)
            endDate = endOfDay(endDate);
        } else {
            endDate = new Date(startDate.getTime())
            endDate.setDate(endDate.getDate() + 6)
            endDate = endOfDay(endDate);
        }
        // Check if the requested range matches what the UI is already showing,
        // and confirm that we actually loaded that range from the server.
        const sameRange = isEqual(cStartDate, startDate) && isEqual(cEndDate, endDate);
        const hasLoadedRange = (
            loadedRangeRef.current.start != null &&
            loadedRangeRef.current.end != null &&
            isEqual(loadedRangeRef.current.start, startDate) &&
            isEqual(loadedRangeRef.current.end, endDate)
        );

        // Avoid skipping reload when dates match but shifts were not actually loaded.
        // This protects date selection from a false no-op path after state resets/refresh edge cases.
        if (sameRange && hasLoadedRange && shiftStoreRef.current.length > 0) {
            setCStartDate(startDate)
            setCEndDate(endDate) // Keep ordering consistent with start->end to reduce transient inversions.
            console.info("No update required; requested payroll range is already loaded")
            return
        }

        setCStartDate(startDate)
        setCEndDate(endDate) // Keep ordering consistent with start->end to reduce transient inversions.
        loadShifts(true, startDate, endDate).then(() => {
            setHistory([])
        });




    }

    const updateShiftInfo = (shiftId: string, data: Partial<IEvent>) => {
        const cpShifts = [...shiftStore]
        const updatedShiftIndex = cpShifts.findIndex(shift => shift.id === shiftId)

        if (updatedShiftIndex < 0) {
            return
        }

        setShiftStore({ update: [{ ...cpShifts[updatedShiftIndex], ...data, loading: false }] })

        return cpShifts[updatedShiftIndex];
    }

    const defineUserAvailability = (startDate: Date, endDate: Date, cResourceId: string, eventId: string): IAvailability[] => {
        const resources = getAllResources()
        const otherSiteCount = otherSiteShiftStore.length
        const resourceKey = resources.map((resource) => resource.id).join('|') // Key based on resource IDs; changes to the resource set invalidate the cache.
        const revision = availabilityCacheRevisionRef.current
        // Create a composite key. Any change in these inputs will invalidate the cache.
        const resourcesRevision = getResourcesRevision()
        const key = `${startDate.toISOString()}|${endDate.toISOString()}|${eventId}|${resourceKey}|${shiftStoreRef.current.length}|${otherSiteCount}|${revision}|${resourcesRevision}`
        if (availabilityCacheRef.current.key === key) {
            return resources.map((resource) => ({
                resourceId: resource.id,
                availability: availabilityCacheRef.current.map.get(resource.id) ?? EEventStatus.valid
            }))
        }

        const map = new Map<string, EEventStatus>()
        resources.forEach((resource) => {
            if (cResourceId === resource.id && resource.availabilities) {
                const available = resource.availabilities?.find(x => x.timeCode == 'Available' && x.start >= startDate && endDate <= x.end);
                map.set(resource.id, available ? EEventStatus.valid : EEventStatus.error)
                return
            }

            map.set(resource.id, checkShiftLocationIsValid(startDate, endDate, eventId, resource.id))
        })

        availabilityCacheRef.current = { key, map }
        return resources.map((resource) => ({
            resourceId: resource.id,
            availability: map.get(resource.id) ?? EEventStatus.valid
        }))
    }

    // Memoize shifts for the calendar to avoid re-compute churn.
    const memoShiftsForCalendarView = useMemo(() => {
        return getShiftsForView()
    }, [shiftStore, cEndDate, cStartDate, draftStatus, loader])

    const callbackOtherSiteShifts = useCallback(() => {
        return getOtherSiteShiftsForView()
    }, [otherSiteShiftStore, getOtherSiteShiftsForView, cEndDate, cStartDate, loader])

    const memoCallbackOtherSiteShifts = useMemo(callbackOtherSiteShifts, [callbackOtherSiteShifts])

    const calculateCost = async () => {
        const calculateMessage = 'Calculating cost...'
        setLoading({ loading: true, message: calculateMessage });
        const updatedShifts: IEvent[] = [];
        const shifts = shiftStore.filter(x => x.resourceId != '0' && x.BookingId != '').map<Partial<IRosteringBookingViewModel>>(x => {
            return { bookableResourceId: x.resourceId, end: x.end, start: x.start, id: x.id, }
        });
        try {
            // Use a longer timeout so larger batches can finish.
            const calculatedShiftResponse = (await postOrThrow(
                '/Rostering/calclatecosts',
                shifts,
                undefined,
                30000
            )) as IServiceResponse<IRosteringBookingViewModel[]>;
            if (!calculatedShiftResponse.success) {
                // TODO: error handling;
                return;
            }
            const calculatedShifts = calculatedShiftResponse.body;
            shiftStore.forEach((s) => {
                const cs = calculatedShifts.find(x => x.id == s.id);
                if (cs) {
                    updatedShifts.push({
                        ...s,
                        actualCost: cs.cost,
                        displayCost: cs.cost
                    })
                }
            });

            setShiftStore({ update: updatedShifts })

            return true;
        } catch (e: any) {
            toast('error', 'Failed to calculate cost. Please try again.')
        } finally {
            // Avoid stale-closure issues: only clear if we are still showing the cost loader.
            setLoading((prev) => (prev.message === calculateMessage ? { loading: false, message: '' } : prev))
        }
    };

    const checkIfShiftAllowed = async (event: IEvent) => {
        if (!event.bidders) {
            return
        }
        const bidders: IBookableResourceBooking[] = []

        for (const bidder of event.bidders) {
            const assignedShifts = getResourceShifts(bidder.bookableResourceId);
            const response = (await post(`/Rostering/bookableresource/${bidder.bookableResourceId}/isshiftAllowed?jobId=${event?.id}`, assignedShifts)) as IServiceResponse<IRosterValidationResponse>;
            if (response.success && response.body.status == 'Failed') {
                toast('error', response.body.errors[0].message);
                continue;
            }
            const errors = response.body.errors
            errors.forEach((error) => {
                switch (error.errorType) {
                    case RosterValidationErrorType.OverTime: {
                        bidder.isOverTime = true
                        break
                    }
                    case RosterValidationErrorType.MissingSkills: {
                        bidder.missingSkills = true
                        break
                    }
                    case RosterValidationErrorType.Unavailable: {
                        bidder.unavailable = true
                        break
                    }
                    case RosterValidationErrorType.ExceededMaxWorkingHours: {
                        bidder.exceededMaxWorkingHours = true
                    }
                }
            })
            bidders.push(bidder);
        }

        updateShiftInfo(event.id, { bidders })
    }
    const publishShift = async (shiftId: string) => {
        let event = shiftStore.find(x => x.id == shiftId) as IEvent;


        const confirmation = await Swal.fire({
            title: 'Are you sure you want to publish the shift?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: theme.palette.themePrimary,
            cancelButtonColor: theme.palette.tealLight,
            confirmButtonText: 'Publish shift'
        })
        if (confirmation.isConfirmed) {
            let success = await publish([event]);
            if (!success) return;
            event.bookableResourceStatus = EBookingStatus.Scheduled;
            event.changeStatus = ChangeStatus.UnChanged;
            updateShiftInfo(shiftId, event);
        }

    }

    const approveRejectCancellationRequest = async (event: IEvent, approved: boolean, comments: string): Promise<boolean> => {

        var bookableResourceData: any = {
            bookableresourcebookingid: event.BookingId,
            illumina_cancellationresponsecomments: comments,
            illumina_cancellationrequeststatus: approved ? ShiftCancellationRequestStatus.Approved : ShiftCancellationRequestStatus.Declined,
            illumina_cancellationresponseresolvedon: null
        };
        if (approved) {
            var bookingStatusList = await getBookingStatusList();
            let cancelStatus = bookingStatusList.find(x => {
                const statusName = x.name.toLocaleLowerCase()
                return statusName === 'canceled' || statusName === 'cancelled'
            })
            bookableResourceData['BookingStatus@odata.bind'] = `/bookingstatuses(${cancelStatus?.bookingstatusid})`;

        }
        let r = await patch('/Entity/bookableresourcebookings/' + event.BookingId, bookableResourceData);
        if (!r?.success)
            return false;

        // Pull the latest server state after approval/decline.
        // Refresh the shift after validation to capture any server updates.
        await refreshShiftFromApi(event.id);
        return true;
    }

    const patchShift = async (shiftId: string, shiftData: Partial<IEvent>, isReloadShifts: boolean = true) => {
        var workOrderData :any = {};
        var bookableResourceData = {};
        Object.entries(shiftData).forEach(keyPair => {
            if (!(Object.keys(EEventToDBMap).includes(keyPair[0]))) return;
            if (keyPair[0] === 'bookableResourceStatus') {
                bookableResourceData = {
                    ...bookableResourceData,
                    [EEventToDBMap[keyPair[0] as 'bookableResourceStatus']]: `/bookingstatuses(${keyPair[1]})`,
                    duration: shiftData.shiftLength,
                    starttime: shiftData.start,
                    endtime: shiftData.end
                }

                return;
            }

            workOrderData = {
                ...workOrderData,
                [EEventToDBMap[keyPair[0] as 'isJobBidding' | 'start' | 'end' | 'description' | 'shiftLength']]: keyPair[1],
                msdyn_timefrompromised: shiftData.start,
                msdyn_timetopromised: shiftData.end,
                ...(shiftData.isClientLateCancel !== undefined
                    ? { illumina_isclientlatecancel: !!shiftData.isClientLateCancel }
                    : {})
            }
        });

        var workOrderResponse;
        var bookableResourceResponse;

        if (Object.entries(workOrderData).length) {
            workOrderResponse = patch('/Entity/msdyn_workorders/' + shiftId, workOrderData);
        }

        if (Object.entries(bookableResourceData).length) {
            if (shiftData.BookingId) {
                bookableResourceResponse = patch('/Entity/bookableresourcebookings/' + shiftData.BookingId, bookableResourceData);
            }
        }

        await workOrderResponse;
        await bookableResourceResponse;

        if (isReloadShifts) {
            // Re-fetch to ensure the UI shows server truth.
            await refreshShiftFromApi(shiftId);
        }
    }


    const createLateClientCancelTimeEntry = async (shift: IEvent) => {
        let jobType = JobType[shift.jobType.replace(/ /g, '') as keyof typeof JobType];
        if (jobType != JobType.ResidentialDisabilityandAgedCareService) return;

        let workOrderType = shift.workOrderType.msdyn_name.replace(/ /g, '');
        let timeEntryType = ETimesheetEntryType.Work;

        switch (workOrderType) {
            case EWorkWorderType[0]: //Active
                timeEntryType = ETimesheetEntryType.Work;
                break;

            case EWorkWorderType[1]: // On Call
                timeEntryType = ETimesheetEntryType.OnCallActive;
                break;

            case EWorkWorderType[2]: // Sleepover
                timeEntryType = ETimesheetEntryType.SleepoverActive;
                break;

                Default:
                timeEntryType = ETimesheetEntryType.Work

        }

        let timeEntry: ITimeEntry = {
            msdyn_entrystatus: ETimesheetEntryStatus.Draft,
            msdyn_type: timeEntryType,
            msdyn_start: shift.start,
            msdyn_end: shift.end,
            msdyn_description: "Shift " + shift.title + " Late Cancelled by Client",
            msdyn_duration: shift.shiftLength / 60,
            illumina_netduration: shift.shiftLength / 60,
            "msdyn_bookableresource@odata.bind": "/bookableresources(" + shift.resourceId + ")",
            //"msdyn_BookableResourceBooking@odata.bind": "/bookableresourcebookings(" + shift.BookingId + ")", //booking is already cancelled, so no need to associate the booking
            "msdyn_WorkOrder@odata.bind": "/msdyn_workorders(" + shift.id + ")"
        }

        await post('/Entity/msdyn_timeentries', timeEntry);
    }

    const calculateContractedHoursPerDay = (startDate: Date, endDate: Date, fromDate: Date, toDate: Date) => {
        let duration = 0;
        if (endDate <= fromDate || startDate >= toDate) {
            duration += 0;
        } else if (startDate <= fromDate && endDate >= toDate) {
            duration += differenceInMinutes(toDate, fromDate);;
        } else {
            duration += differenceInMinutes(endDate, startDate);;
        }
       return duration;
    }

    const sumDailyHours = (item: any, fromDate: Date, toDate: Date): number => {
        const startDate = new Date(item.illumina_startdate);
        const endDate = new Date(item.illumina_enddate);
        let dateDiff = limitDateRangeToOneYear(startDate, endDate);
        let totalDuration = 0;
      
        for (let i = 0; i < dateDiff; i++) {
          const currentStartDate = addDays(startDate, i);
          const currentEndDate = new Date(currentStartDate);
          currentEndDate.setHours(endDate.getHours(), endDate.getMinutes(), 0, 0);

          let calculatedStartDate;
          let calculatedEndDate;
          if (item.illumina_allday) {
            const currentStartDay = startOfDay(currentStartDate);
            const currentEndDay = endOfDay(currentEndDate);
            calculatedStartDate = currentStartDay;
            calculatedEndDate = currentEndDay;
          } else {
            calculatedStartDate = currentStartDate;
            calculatedEndDate = currentEndDate;
          }

          totalDuration += calculateContractedHoursPerDay(calculatedStartDate, calculatedEndDate, fromDate, toDate);
        }
      
        return totalDuration;
    }

    const sumWeeklyHours = (item: any, fromDate: Date, toDate: Date) => {
        const startDate = new Date(item.illumina_startdate);
        const endDate = new Date(item.illumina_enddate);
        let dateDiff = limitDateRangeToOneYear(startDate, endDate);
        let totalDuration = 0;
        let weekNo = 1;
        const repeatDays = item.illumina_days
          ? item.illumina_days.split(',').map((d: string) => daysOfWeekMapping().get(parseInt(d)))
              .filter((d: number) => d !== undefined)
          : [];
      
        for (let i = 0; i < dateDiff; i++) {
          const currentDate = addDays(startDate, i);
      
          // start of week is tuesday
          if (i > 0 && currentDate.getDay() === 2) {
            weekNo++;
          }

          if ((weekNo - 1) % (item.illumina_repeatoccurence ?? 1) === 0) {
            const dayOfWeek = currentDate.getDay();
            if (repeatDays.includes(dayOfWeek)) {
              const currentStartDate = currentDate;

              let calculatedStartDate;
              let calculatedEndDate;
              
              let start = currentStartDate;
              let end = currentStartDate;
              if (item.illumina_allday) {
                start = startOfDay(currentStartDate);
                end = endOfDay(currentStartDate);
              } else {
                end = new Date(currentDate);
                end.setHours(endDate.getHours(), endDate.getMinutes(), 0, 0);

                if (end < start) {
                    end = addDays(end, 1);
                }
              }

              calculatedStartDate = start;
              calculatedEndDate = end;
              totalDuration += calculateContractedHoursPerDay(calculatedStartDate, calculatedEndDate, fromDate, toDate);
            }
          }
        }
      
        return totalDuration;
    }

    const sumFortnightlyHours = (
        item: any,
        fromDate: Date,
        toDate: Date
      ): number => {
        const startDate = new Date(item.illumina_startdate);
        const endDate = new Date(item.illumina_enddate);
        let weekNo = 1;
        let dateDiff = limitDateRangeToOneYear(startDate, endDate);
        let totalDuration = 0;
      
        for (let i = 0; i < dateDiff; i++) {
          const currentDate = addDays(startDate, i);
          // day of week start on tuesday
          if (i > 0 && currentDate.getDay() === 2) {
            weekNo++;
          }
      
          let calculatedStartDate = currentDate;
          let calculatedEndDate = currentDate;
          if (item.illumina_allday) {
            calculatedStartDate = startOfDay(currentDate);
            calculatedEndDate = endOfDay(currentDate);
          } else {
            calculatedEndDate = new Date(currentDate);
            calculatedEndDate.setHours(endDate.getHours(), endDate.getMinutes(), 0, 0);

            if (calculatedEndDate < calculatedStartDate) {
              calculatedEndDate = addDays(calculatedEndDate, 1);
            }
          }
      
          const fortnightlyWeek1 = item.illumina_week1 ? item.illumina_week1.split(',').map((d: string) => daysOfWeekMapping().get(parseInt(d))).filter((d: number) => d !== undefined) : [];
          const fortnightlyWeek2 = item.illumina_week2 ? item.illumina_week2.split(',').map((d: string) => daysOfWeekMapping().get(parseInt(d))).filter((d: number) => d !== undefined) : [];
      
          if (weekNo % 2 !== 0) {
            if (fortnightlyWeek1.includes(currentDate.getDay())) {
              totalDuration += calculateContractedHoursPerDay(calculatedStartDate, calculatedEndDate, fromDate, toDate);
            }
          } else {
            if (fortnightlyWeek2.includes(currentDate.getDay())) {
                totalDuration += calculateContractedHoursPerDay(calculatedStartDate, calculatedEndDate, fromDate, toDate);
            }
          }
        }
      
        return totalDuration;
    }

    const daysOfWeekMapping = () => {
        return new Map<number, number>([
            [WeekDay.Sunday, 0],
            [WeekDay.Monday, 1],
            [WeekDay.Tuesday, 2],
            [WeekDay.Wednesday, 3],
            [WeekDay.Thursday, 4],
            [WeekDay.Friday, 5],
            [WeekDay.Saturday, 6],
        ]);
    }

    const enum WeekDay {
        Monday = 390950000,
        Tuesday = 390950001,
        Wednesday = 390950002,
        Thursday = 390950003,
        Friday = 390950004,
        Saturday = 390950005,
        Sunday = 390950006
    }

    const enum RepeatPattern {
        None = 0,
        Daily = 782790000,
        Weekly = 782790001,
        Monthly = 782790002,
        Fortnightly = 782790003,
    }

    const toggleShowOtherSiteShifts = useCallback(() => {
        setShowOtherSiteShifts(!showOtherSiteShifts)
    }, [showOtherSiteShifts, setShowOtherSiteShifts])

    return (
        <shiftsContext.Provider value={{
            saveDraft,
            startDate: cStartDate,
            endDate: cEndDate,
            loader,
            moveShift,
            getPayrollData: getPayrollData,
            getShiftsForCalendarView: () => memoShiftsForCalendarView,
            getOtherSiteShiftsForCalendarView: () => memoCallbackOtherSiteShifts,
            getResourceData: getResourcesData,
            checkShiftLocationIsValid,
            checkShiftUnsavedChanges,
            markAllShiftsAsUnchanged: markAllShiftsAsUnchanged,
            updateShiftInfo,
            getShift,
            draftStatus,
            setRosterboardDragActive,
            setCalendarDates,
            undoShiftMove: undoMove,
            validateAndPublishShifts: validateAndPublishShifts,
            deleteDraft,
            loadDraft,
            reloadShifts,
            checkRequiredCharacteristics,
            defineUserAvailability,
            checkIsModified,
            calculateCost,
            getResourceShifts,
            getEnvironmentInfo,
            checkIfShiftAllowed,
            publishShift,
            publishSingleShift,
            patchShift,
            createLateClientCancelTimeEntry,
            showOtherSiteShifts,
            toggleShowOtherSiteShifts,
            view,
            setView,
            loadingPayrollData,
            siteLocation,
            validated,
            currentPayrollRange,
            getShiftTypes,
            approveRejectCancellationRequest,
            getBookingStatusList,
            isCancellationReviewPending,
            validateAllShifts,
            removeShift: removeShift,
            payrollCycles

        }}>
            {props.children}
        </shiftsContext.Provider>
    )

}

// function getRandomInt(max: number) {
//     return Math.floor(Math.random() * max);
// }

export { ShiftsProvider, shiftsContext }
